import React, { useState, useRef, useCallback, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { googleCalendarAPI } from '../../../services/api';
import { IframeDetector } from '../../../utils/iframeDetector';
import { isKilnAppWebView } from '../../../utils/webViewDetector';
import { OAuthPopupHandler } from '../../../utils/oauthPopupHandler';
import { redirectTo } from '../../../utils/externalRedirect';
import useGoogleCalendarConnection from '../../../hooks/useGoogleCalendarConnection';
import logger from '../../../utils/logger';
import './ConnectCalendarButton.css';

// Google Calendar icon (public/gcal-icon.png — the same icon the RCS card and SMS link preview use)
const GoogleCalendarMark = () => (
  <img
    className="connect-calendar-mark"
    src={`${process.env.PUBLIC_URL || ''}/gcal-icon.png`}
    alt=""
    aria-hidden="true"
  />
);

const START_FAILED_MESSAGE = "Couldn't start the Google Calendar connection. Please try again.";

/**
 * Shown in the chat in place of the assistant's link to /google-calendar.
 * Starts Google's sign-in straight away, using the same three paths as the
 * Connect button on the Google Calendar page:
 *   - browser: the tab goes to Google, and Google returns to the calendar page
 *   - iframe (OfficeRND): Google opens in a popup and the chat stays put
 *   - mobile app: Google refuses to sign in inside a WebView, so open the
 *     calendar page in auto-connect mode, which hands off to the phone's browser
 */
const ConnectCalendarButton = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('idle'); // idle | connecting | connected
  const busyRef = useRef(false);
  // Several messages can carry the link, so each button needs its own id
  const subtextId = useId();
  const { connected, needsReconnect, email } = useGoogleCalendarConnection();

  const connectInPopup = async () => {
    const { auth_url: authUrl, state, popup_id: popupId } = await googleCalendarAPI.connectCalendarIframe();
    if (!authUrl) throw new Error('No auth_url from backend');

    sessionStorage.setItem('oauth_state', state);
    sessionStorage.setItem('oauth_popup_id', popupId || '');

    try {
      const result = await new OAuthPopupHandler().openPopup(authUrl, popupId);
      if (!result.success) throw new Error(result.message || 'Failed to complete authorization');

      logger.success('ConnectCalendarButton', 'Calendar connected from chat (popup)');
      toast.success(result.message || 'Calendar connected successfully');
      setStatus('connected');
    } catch (popupError) {
      setStatus('idle');
      if (popupError.message === 'OAuth cancelled - popup closed by user') {
        logger.info('ConnectCalendarButton', 'User closed the Google popup');
      } else if (popupError.message === 'Popup blocked') {
        toast.error('Please allow popups for this site and try again');
      } else if (popupError.message === 'OAuth timeout - no response received') {
        toast.error('Connection timed out. Please try again.');
      } else {
        logger.error('ConnectCalendarButton', 'Google popup failed', popupError);
        toast.error(popupError.message || 'Failed to connect calendar');
      }
    }
  };

  const handleClick = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setStatus('connecting');

    try {
      if (isKilnAppWebView() && !IframeDetector.isIframe()) {
        logger.info('ConnectCalendarButton', 'App WebView - opening calendar page to connect in the browser');
        navigate('/google-calendar?auto_connect=true');
        return;
      }

      if (IframeDetector.isIframe()) {
        await connectInPopup();
        return;
      }

      const { auth_url: authUrl, state } = await googleCalendarAPI.connectCalendar();
      if (!authUrl) throw new Error('No auth_url from backend');

      sessionStorage.setItem('oauth_state', state);
      logger.info('ConnectCalendarButton', 'Redirecting to Google sign-in from chat');
      // Stays "connecting" while the browser leaves the page
      redirectTo(authUrl);
    } catch (error) {
      logger.error('ConnectCalendarButton', 'Failed to start calendar connection', error);
      toast.error(START_FAILED_MESSAGE);
      setStatus('idle');
    } finally {
      busyRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Already connected (checked with the backend) or just connected in the popup:
  // nothing left to click
  if (connected || status === 'connected') {
    return (
      <div className="connect-calendar">
        <div className="connect-calendar-btn connect-calendar-btn--done" role="status">
          <span className="connect-calendar-check" aria-hidden="true">
            <Check size={16} strokeWidth={3} />
          </span>
          <span className="connect-calendar-text">
            <span className="connect-calendar-label">Google Calendar connected</span>
            {email && <span className="connect-calendar-subtext">{email}</span>}
          </span>
        </div>
      </div>
    );
  }

  const isConnecting = status === 'connecting';
  // Sync off on every calendar: same Google sign-in, worded as a reconnect
  const label = needsReconnect ? 'Reconnect Google Calendar' : 'Connect Google Calendar';
  const subtext = needsReconnect
    ? `${email || 'Your calendar'} stopped syncing — reconnect with the same account`
    : 'add, update, and reschedule events';

  return (
    <div className="connect-calendar">
      <button
        type="button"
        className="connect-calendar-btn"
        onClick={handleClick}
        disabled={isConnecting}
        aria-busy={isConnecting}
        aria-label={label}
        aria-describedby={subtextId}
      >
        {isConnecting
          ? <span className="connect-calendar-spinner" aria-hidden="true" />
          : <GoogleCalendarMark />}
        <span className="connect-calendar-text">
          <span className="connect-calendar-label">
            {isConnecting ? 'Connecting…' : label}
          </span>
          <span id={subtextId} className="connect-calendar-subtext">
            {subtext}
          </span>
        </span>
      </button>
    </div>
  );
};

export default ConnectCalendarButton;
